import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import type { WorkspaceAttachment } from "common/types";

async function uploadAttachmentRequest(
  workspaceId: string,
  file: Blob,
  abortSignal?: AbortSignal,
): Promise<WorkspaceAttachment & { url: string }> {
  const formData = new FormData();
  formData.append("file", file);

  const { attachment } = await post(
    `workspaces/${workspaceId}/attachments/upload`,
    formData,
    abortSignal,
  );
  return attachment;
}

export default function useWorkspaceAttachmentsMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { attachmentsKeys } = getQueryKeys(workspaceId);

  const { mutateAsync: uploadAttachment, isPending: isUploadingAttachment } =
    useMutation({
      mutationFn: ({
        file,
        abortSignal,
      }: {
        file: Blob;
        abortSignal?: AbortSignal;
      }) => uploadAttachmentRequest(workspaceId, file, abortSignal),
      onSuccess: ({ id, url }, { file }) => {
        queryClient.setQueryData(attachmentsKeys.url(id), url);
        queryClient.setQueryData(["attachmentBlob", url], file);
      },
    });

  return {
    uploadAttachment,
    isUploadingAttachment,
  };
}
